/**
 * §A.5 · Las solicitudes de datos que llegan a la plataforma.
 *
 * ## El plazo no es un dato administrativo
 *
 * «El RGPD da un mes, y una solicitud vencida no es un retraso administrativo: es un
 * incumplimiento con nombre y fecha.» Así que `days_to_due` se pinta como número y en
 * rojo cuando es negativo, y la lista entra ordenada por lo que queda.
 *
 * ## Aquí solo se mira
 *
 * El contrato da a §A **una** ruta: listar. Atender una solicitud se hace desde el
 * club —§P.8 tiene su pantalla, con el club decidiendo sobre sus propios datos— y eso
 * está bien: quien responde a una familia es su club, no nosotros. Lo que necesitamos
 * de este lado es **ver si alguien se está quedando sin plazo**, para llamar antes de
 * que se cumpla.
 *
 * Se dice en pantalla, porque una lista sin botones invita a buscar el botón.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import {
  etiquetaDeEstadoDeSolicitud,
  etiquetaDeSolicitud,
  type EstadoDeSolicitud,
  type SolicitudDeDatos,
} from '@/backoffice/tipos';

const ESTADOS: readonly { valor: string; etiqueta: string }[] = [
  { valor: '', etiqueta: 'Todas' },
  { valor: 'received', etiqueta: 'Recibidas' },
  { valor: 'in_progress', etiqueta: 'En curso' },
  { valor: 'completed', etiqueta: 'Resueltas' },
  { valor: 'rejected', etiqueta: 'Rechazadas' },
];

export function SolicitudesRgpd() {
  const [estado, setEstado] = useState('');

  const solicitudes = useQuery({
    queryKey: ['admin', 'rgpd', estado],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['data-requests'].get({
        query: {
          ...(estado === '' ? {} : { status: estado as EstadoDeSolicitud }),
          limit: 200,
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SolicitudDeDatos[];
    },
  });

  const vencidas = (solicitudes.data ?? []).filter(
    (s) => s.days_to_due !== null && s.days_to_due < 0 && !s.completed_at
  );

  return (
    <section>
      <h1>Solicitudes de datos</h1>

      <p className="tenue">
        Lo que piden las familias y el personal sobre sus propios datos, en los
        doscientos clubes. <strong>Aquí solo se mira</strong>: quien responde a
        una familia es su club, desde su propia pantalla. Esto es para llamar
        antes de que se cumpla un plazo.
      </p>

      {/* Lo que hay que hacer hoy, antes de la tabla. */}
      {vencidas.length > 0 && (
        <div className="aviso aviso-grave" role="alert">
          <strong>
            {vencidas.length === 1
              ? 'Hay una solicitud fuera de plazo'
              : `Hay ${vencidas.length} solicitudes fuera de plazo`}
          </strong>
          . El RGPD da un mes y ya ha pasado:{' '}
          {vencidas
            .slice(0, 3)
            .map((s) => s.academy_name ?? 'un club sin nombre')
            .join(', ')}
          {vencidas.length > 3 ? ' y alguno más.' : '.'}
        </div>
      )}

      <div className="filtros">
        <label>
          Estado
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            {ESTADOS.map((e) => (
              <option key={e.valor} value={e.valor}>
                {e.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {solicitudes.isPending && <Cargando filas={4} etiqueta="Cargando" />}

      {solicitudes.isError && (
        <PanelError
          problema={aProblema(solicitudes.error)}
          reintentar={() => void solicitudes.refetch()}
        />
      )}

      {solicitudes.data &&
        (solicitudes.data.length === 0 ? (
          <Vacio
            titulo="Ninguna solicitud"
            explicacion="Es lo normal: una solicitud de datos es algo que pasa unas pocas veces al año en un club, y aquí se ven las de todos."
          />
        ) : (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Quién</th>
                  <th>Qué pide</th>
                  <th>Estado</th>
                  <th>Pedida</th>
                  <th className="num">Plazo</th>
                </tr>
              </thead>
              <tbody>
                {solicitudes.data.map((s) => {
                  const fuera =
                    s.days_to_due !== null &&
                    s.days_to_due < 0 &&
                    !s.completed_at;
                  return (
                    <tr
                      key={s.id}
                      className={fuera ? 'fila-marcada' : undefined}
                    >
                      <td>{s.academy_name ?? '—'}</td>
                      <td>{s.user_email ?? '—'}</td>
                      <td>{etiquetaDeSolicitud(s.kind)}</td>
                      <td>
                        <span
                          className={`pastilla ${
                            s.status === 'completed'
                              ? 'pastilla-bien'
                              : s.status === 'rejected'
                                ? 'pastilla-paused'
                                : 'pastilla-info'
                          }`}
                        >
                          {etiquetaDeEstadoDeSolicitud(s.status)}
                        </span>
                      </td>
                      <td>{fecha(s.requested_at)}</td>
                      <td className="num">
                        {/*
                          El número y no un color: «vencida hace 4 días» se puede
                          decir por teléfono, y «está en rojo» no.
                        */}
                        {s.completed_at
                          ? '—'
                          : s.days_to_due === null
                            ? 'Sin plazo'
                            : s.days_to_due < 0
                              ? `Vencida hace ${Math.abs(s.days_to_due)} d`
                              : `${s.days_to_due} d`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
